/**
 * Central de Clientes - gestao completa do cliente.
 * Reutiliza endpoints: /clients, /units, /areas, /subareas, /devices.
 * Contrato de módulos e usuários: Central Administrativa (aba Acessos).
 * Hierarquia: Cliente -> Unidade -> Area -> Subarea -> Dispositivo
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2, Factory, Map, Grid3X3, Cpu, Users, History,
  Plus, Building, Search,
} from "lucide-react";
import ClientForm from "./ClientForm";
import UnitsTab from "./UnitsTab";
import AreasTab from "./AreasTab";
import SubareasTab from "./SubareasTab";
import DevicesTab from "./DevicesTab";
import AccessTab from "./AccessTab";
import HistoryTab from "./HistoryTab";

const TABS = [
  { key: "cadastro", label: "Cadastro", icon: Building2 },
  { key: "unidades", label: "Unidades", icon: Factory },
  { key: "areas", label: "Areas", icon: Map },
  { key: "subareas", label: "Subareas", icon: Grid3X3 },
  { key: "dispositivos", label: "Dispositivos", icon: Cpu },
  { key: "acessos", label: "Acessos", icon: Users },
  { key: "historico", label: "Historico", icon: History },
];

export default function ClientCentral() {
  const { clientId: paramClientId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("cadastro");
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Criar empresa cliente = criar tenant: decisão do backend (só a plataforma).
  const canCreateClient = useCapabilities(["clientes.create"]).can("clientes.create");

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      const list = Array.isArray(data) ? data : data?.items || [];
      setClients(list);
      // Prioriza o cliente vindo da rota /central-clientes/:clientId
      if (list.length > 0) {
        const initialId =
          paramClientId && list.some((c) => c.id === paramClientId)
            ? paramClientId
            : selectedClientId && list.some((c) => c.id === selectedClientId)
              ? selectedClientId
              : list[0]?.id;
        setSelectedClientId(initialId);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, paramClientId]);

  useEffect(() => { loadClients(); }, []);

  useEffect(() => {
    if (selectedClientId) {
      setSelectedClient(clients.find((c) => c.id === selectedClientId) || null);
    }
  }, [selectedClientId, clients]);

  const filteredClients = clients.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.code || "").toLowerCase().includes(q) ||
      (c.cnpj || "").toLowerCase().includes(q)
    );
  });

  const handleClientSaved = (saved) => {
    loadClients();
    if (saved?.id) setSelectedClientId(saved.id);
  };

  const handleClientDeleted = (id) => {
    setClients((prev) => prev.filter((c) => c.id !== id));
    if (selectedClientId === id) {
      setSelectedClientId(null);
      setSelectedClient(null);
    }
  };

  const renderTab = () => {
    if (!selectedClientId) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Building className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-sm">Selecione um cliente para gerenciar</p>
        </div>
      );
    }
    switch (tab) {
      case "cadastro":
        return <ClientForm client={selectedClient} onSaved={handleClientSaved} onDeleted={handleClientDeleted} />;
      case "unidades":
        return <UnitsTab clientId={selectedClientId} />;
      case "areas":
        return <AreasTab clientId={selectedClientId} />;
      case "subareas":
        return <SubareasTab clientId={selectedClientId} />;
      case "dispositivos":
        return <DevicesTab clientId={selectedClientId} />;
      case "acessos":
        return <AccessTab clientId={selectedClientId} />;
      case "historico":
        return <HistoryTab clientId={selectedClientId} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Central de Clientes</h1>
          <p className="text-sm text-muted-foreground">
            Gestao completa do cliente e sua estrutura operacional
          </p>
        </div>
        {canCreateClient && (
          <Button size="sm" onClick={() => { setSelectedClient(null); setSelectedClientId("new"); }} data-testid="clients-new">
            <Plus className="h-4 w-4 mr-1" />Novo Cliente
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <Card className="p-3 h-fit">
          <div className="relative mb-3">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
          </div>
          {loading ? (
            <div className="space-y-2">{[1,2,3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filteredClients.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">Nenhum cliente encontrado</p>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {filteredClients.map((client) => {
                const active = client.id === selectedClientId;
                return (
                  <button key={client.id} onClick={() => { setSelectedClientId(client.id); navigate(`/central-clientes/${client.id}`); }}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${active ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                    data-testid={`central-client-item-${client.id}`}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{client.name}</p>
                        <p className={`text-xs truncate ${active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {client.code || client.cnpj || "-"}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4">
          {selectedClientId === "new" ? (
            <ClientForm client={null} onSaved={handleClientSaved} onDeleted={handleClientDeleted} />
          ) : selectedClient ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">{selectedClient.name}</h2>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {selectedClient.code && <Badge variant="outline">{selectedClient.code}</Badge>}
                      {selectedClient.cnpj && <span>{selectedClient.cnpj}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 border-b border-border pb-2">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`central-tab-${t.key}`}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                      <Icon className="h-4 w-4" />{t.label}
                    </button>
                  );
                })}
              </div>
              {renderTab()}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Building className="h-12 w-12 mb-3 opacity-40" />
              <p className="text-sm">Selecione um cliente para gerenciar</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
